import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {getGroupedData} from "../../../api/duckdb/getGroupedData";
import {getPaginatedData} from "../../../api/duckdb/getPaginatedData";
import {pathKey} from "./asyncTableGrouping";

const EMPTY_PATH = [];

/**
 * Drzewo grup TEMPTABLE ładowane leniwie (duckQueryTempTableGroups + duckQueryTempTableData z GroupKeys).
 *
 * - poziom 0: strona grup (`top`) + wiersz "Razem" (`total`, tylko z pierwszej strony),
 * - rozwinięta grupa (`nodes[pathKey(path)]`): podgrupy (E1) albo wiersze (E2), gdy grupa jest z ostatniego poziomu,
 * - zmiana grupowania, filtrów, agregatów lub ResultID resetuje całe drzewo,
 * - zmiana sortowania wierszy zostawia grupy i rozwinięcia: otwarte grupy wierszy ładują się od nowa (strona 1),
 *   a zamknięte tracą zapamiętane wiersze (pobiorą je przy rozwinięciu),
 * - każda grupa ma własny stan ładowania/błędu - błąd jednej nie blokuje reszty.
 *
 * @param {Object} params
 * @param {string} params.resultId
 * @param {Array<string|Object>} params.groupFields poziomy GroupFields (pole albo pole z przedziałami reguły);
 *        pusta lista = brak grupowania (hook nic nie pobiera)
 * @param {Array} params.filter filtr w formacie API
 * @param {Array} params.sortFields sortowanie wierszy
 * @param {Array} params.aggregations agregacje wierszy grup
 * @param {Array<Object|undefined>} [params.groupSorts] `GroupSort` każdego poziomu (indeks = poziom)
 * @param {number} params.groupsLimit grup na stronę na poziomie 0
 * @param {number} params.rowsLimit podgrup / wierszy na stronę w rozwiniętej grupie
 * @param {boolean} [params.ready] false - wstrzymuje pobieranie (np. do wczytania zapisanych widoków)
 */
export default function useAsyncTableGroups({
    resultId,
    groupFields,
    filter,
    sortFields,
    aggregations,
    groupSorts,
    groupsLimit,
    rowsLimit,
    ready = true,
}) {
    // `ready` - np. zapisane widoki tabeli jeszcze się wczytują; do tego czasu nic nie wysyłamy
    const enabled = ready && !!resultId && groupFields.length > 0;

    const [top, setTop] = useState({page: 1, loading: false, error: false});
    const [total, setTotal] = useState(null);
    const [nodes, setNodes] = useState({});

    // aktualne węzły dla callbacków - updater setState w React 18 nie wykonuje się od razu
    const nodesRef = useRef(nodes);
    nodesRef.current = nodes;

    // każdy reset zwiększa generację - odpowiedzi ze starego drzewa są ignorowane
    const generation = useRef(0);

    // każda zmiana sortowania wierszy - odpowiedzi wierszy w starym sortowaniu są ignorowane
    const rowsGeneration = useRef(0);

    const context = useMemo(
        () => ({
            resultId,
            groupFields,
            filter,
            sortFields,
            aggregations,
            groupSorts,
            groupsLimit,
            rowsLimit,
        }),
        [
            resultId,
            groupFields,
            filter,
            sortFields,
            aggregations,
            groupSorts,
            groupsLimit,
            rowsLimit,
        ]
    );

    const contextRef = useRef(context);
    contextRef.current = context;

    // reset po wartości, nie po referencji - nowa, ale równa tablica (np. filtr) nie przeładowuje drzewa;
    // sortowanie wierszy osobno - nie zmienia grup, więc nie zwija drzewa
    const resetKey = useMemo(() => {
        const {sortFields: rowsSort, ...tree} = context;
        return JSON.stringify(tree);
    }, [context]);

    const sortKey = useMemo(() => JSON.stringify(sortFields), [sortFields]);

    const fetchLevel = useCallback((path, page, isRows) => {
        const ctx = contextRef.current;
        if (isRows) {
            return getPaginatedData({
                ResultID: ctx.resultId,
                StartOffset: (page - 1) * ctx.rowsLimit,
                RecordsCount: ctx.rowsLimit,
                SortFields: ctx.sortFields,
                Filter: ctx.filter,
                GroupKeys: path,
                // definicje poziomów - klucz poziomu z regułą to indeks przedziału, serwer zamienia go na warunek
                GroupFields: ctx.groupFields,
            });
        }
        const limit = path.length === 0 ? ctx.groupsLimit : ctx.rowsLimit;
        return getGroupedData({
            ResultID: ctx.resultId,
            GroupFields: ctx.groupFields,
            GroupKeys: path,
            Filter: ctx.filter,
            // poziom pobieranych grup = długość ścieżki
            GroupSort: ctx.groupSorts?.[path.length],
            Aggregations: ctx.aggregations,
            StartOffset: (page - 1) * limit,
            RecordsCount: limit,
            IncludeTotal: path.length === 0 && page === 1,
        });
    }, []);

    const loadTop = useCallback(
        async (page) => {
            const gen = generation.current;
            setTop((prev) => ({...prev, page, loading: true, error: false}));
            try {
                const response = await fetchLevel(EMPTY_PATH, page, false);
                if (gen !== generation.current) return;
                if (response.Total) setTotal(response.Total);
                setTop({page, loading: false, error: false, response});
            } catch (e) {
                console.error(e);
                if (gen !== generation.current) return;
                setTop((prev) => ({...prev, loading: false, error: true}));
            }
        },
        [fetchLevel]
    );

    const loadNode = useCallback(
        async (path, page, isRows) => {
            const key = pathKey(path);
            const gen = generation.current;
            const rowsGen = rowsGeneration.current;
            // odpowiedź z innego drzewa albo wiersze w starym sortowaniu
            const stale = () =>
                gen !== generation.current ||
                (isRows && rowsGen !== rowsGeneration.current);
            setNodes((prev) => ({
                ...prev,
                [key]: {
                    ...prev[key],
                    path,
                    isRows,
                    expanded: true,
                    page,
                    loading: true,
                    error: false,
                },
            }));
            try {
                const response = await fetchLevel(path, page, isRows);
                if (stale()) return null;
                setNodes((prev) => ({
                    ...prev,
                    [key]: {...prev[key], loading: false, response},
                }));
                return response;
            } catch (e) {
                console.error(e);
                if (stale()) return null;
                setNodes((prev) => ({
                    ...prev,
                    [key]: {...prev[key], loading: false, error: true},
                }));
                return null;
            }
        },
        [fetchLevel]
    );

    // ostatni zastosowany kontekst - odróżnia zmianę samego sortowania od resetu drzewa
    const applied = useRef(null);

    useEffect(() => {
        const previous = applied.current;
        applied.current = {resetKey, sortKey, enabled};
        // reset drzewa przy każdej zmianie kontekstu (poza samym sortowaniem wierszy)
        if (
            !previous ||
            previous.resetKey !== resetKey ||
            previous.enabled !== enabled
        ) {
            generation.current++;
            setNodes({});
            setTotal(null);
            if (!enabled) {
                setTop({page: 1, loading: false, error: false});
                return;
            }
            loadTop(1);
            return;
        }
        if (previous.sortKey === sortKey) return;
        // samo sortowanie wierszy: grupy zostają, otwarte wiersze od nowa, zamknięte bez zapamiętanych wierszy
        rowsGeneration.current++;
        const rowNodes = Object.values(nodesRef.current).filter(
            (node) => node.isRows
        );
        setNodes((prev) => {
            const next = {...prev};
            for (const node of rowNodes) {
                if (!node.expanded) {
                    next[pathKey(node.path)] = {
                        ...node,
                        response: undefined,
                        loading: false,
                    };
                }
            }
            return next;
        });
        rowNodes
            .filter((node) => node.expanded)
            .forEach((node) => loadNode(node.path, 1, true));
    }, [resetKey, sortKey, enabled, loadTop, loadNode]);

    const setTopPage = useCallback((page) => loadTop(page), [loadTop]);

    /**
     * Rozwija grupę. Rozwinięte wcześniej grupy są w cache po ścieżce - wracają bez zapytania.
     * @param {Array} path ścieżka GroupKeys do grupy włącznie
     * @param {boolean} isRows grupa z ostatniego poziomu (IsLeafLevel) - rozwinięcie = wiersze
     * @returns {Promise<Object|null>} odpowiedź z zawartością grupy (z cache albo z API)
     */
    const expand = useCallback(
        (path, isRows) => {
            const key = pathKey(path);
            const cached = nodesRef.current[key];
            if (!cached?.response) {
                return loadNode(path, 1, isRows);
            }
            setNodes((prev) => ({
                ...prev,
                [key]: {...prev[key], expanded: true},
            }));
            return Promise.resolve(cached.response);
        },
        [loadNode]
    );

    const collapse = useCallback((path) => {
        const key = pathKey(path);
        setNodes((prev) =>
            prev[key] ? {...prev, [key]: {...prev[key], expanded: false}} : prev
        );
    }, []);

    const setNodePage = useCallback(
        (path, page) => {
            const node = nodesRef.current[pathKey(path)];
            if (node) loadNode(path, page, node.isRows);
        },
        [loadNode]
    );

    const retry = useCallback(
        (path) => {
            if (path.length === 0) return loadTop(top.page);
            const node = nodesRef.current[pathKey(path)];
            if (node) return loadNode(path, node.page, node.isRows);
        },
        [loadNode, loadTop, top.page]
    );

    const collapseAll = useCallback(() => {
        setNodes((prev) => {
            const next = {};
            for (const key in prev) next[key] = {...prev[key], expanded: false};
            return next;
        });
    }, []);

    // cokolwiek się ładuje (poziom 0 albo zawartość grupy) - np. blokada sortowania w nagłówkach
    const loading = useMemo(
        () => top.loading || Object.values(nodes).some((node) => node.loading),
        [top.loading, nodes]
    );

    return {
        enabled,
        loading,
        top,
        total,
        nodes,
        setTopPage,
        expand,
        collapse,
        setNodePage,
        retry,
        collapseAll,
    };
}
